import {useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {Button, Grid, Link, MenuItem, TextField, Typography} from '@mui/material';
import {makeStyles} from 'tss-react/mui';
import {HowToReg} from '@mui/icons-material';
import {PhoneNumberSubmissionDialog} from '../components/PhoneNumberSubmissionDialog';
import {submitCheckIn} from '../App';

const useStyles = makeStyles()((theme) => {
    return {
        surveyTitle: {
            marginTop: theme.spacing(10),
            marginBottom: theme.spacing(1),
            fontSize: "2em",
            fontWeight: 600,
        },
        rootGridContainer: {
            display: 'flex',
            paddingRight: theme.spacing(3),
            paddingLeft: theme.spacing(3),
        },
        icon: {
            marginRight: theme.spacing(2),
        },
        subtitle: {
            lineHeight: theme.spacing(2)
        },
        surveyButton: {
            marginTop: theme.spacing(4),
            fontWeight: 400,
            textDecoration: "none",
        },
        surveyLink: {
            textDecoration: "none",
        }
    };
});


const apiReasonsForVisit = {
    Pickup: 'pickup',
    Extension: 'extension',
    Rewrite: 'rewrite',
    Return: 'return',
    Maintenance: 'maintenance',
    BillingIssues: 'billing_issues',
    Accident: 'accident',
    VehicleExchange: 'vehicle_exchange',
    Other: 'other',
}

const apiReasonsForReturn = {
    BillingProcess: 'billing_process',
    RentalCostTooHigh: 'rental_cost_too_high',
    LowerEarningsThanExpected: 'lower_earnings_than_expected',
    EvChargingCostTooHigh: 'ev_charging_cost_too_high',
    EvChargingPainPoint: 'ev_charging_pain_point',
    Vacation: 'vacation',
    PrimaryVehicleBackFromShop: 'primary_vehicle_back_from_shop',
    Other: 'other',
}

export const reasonForVisitOptions = [
    {displayValue: 'Pickup', apiValue: apiReasonsForVisit.Pickup},
    {displayValue: 'Weekly Extension', apiValue: apiReasonsForVisit.Extension},
    {displayValue: 'Monthly Rewrite', apiValue: apiReasonsForVisit.Rewrite},
    {displayValue: 'Return', apiValue: apiReasonsForVisit.Return},
    {displayValue: 'Maintenance', apiValue: apiReasonsForVisit.Maintenance},
    {displayValue: 'Billing Issues', apiValue: apiReasonsForVisit.BillingIssues},
    {displayValue: 'Accident', apiValue: apiReasonsForVisit.Accident},
    {displayValue: 'Vehicle Exchange', apiValue: apiReasonsForVisit.VehicleExchange},
    {displayValue: 'Other', apiValue: apiReasonsForVisit.Other},
]

const reasonForReturnOptions = [
    {displayValue: 'Billing Process', apiValue: apiReasonsForReturn.BillingProcess},
    {displayValue: 'Rental Cost Too High', apiValue: apiReasonsForReturn.RentalCostTooHigh},
    {displayValue: 'Lower Earnings Than Expected', apiValue: apiReasonsForReturn.LowerEarningsThanExpected},
    {displayValue: 'EV Charging Cost Too High', apiValue: apiReasonsForReturn.EvChargingCostTooHigh},
    {displayValue: 'EV Charging Pain Point', apiValue: apiReasonsForReturn.EvChargingPainPoint},
    {displayValue: 'Taking a Break / Vacation', apiValue: apiReasonsForReturn.Vacation},
    {displayValue: 'Primary Vehicle Back From Shop', apiValue: apiReasonsForReturn.PrimaryVehicleBackFromShop},
    {displayValue: 'Other', apiValue: apiReasonsForReturn.Other},
]

export default function DriverCheckInPage() {
    const initialValues = {
        name: '',
        reasonForVisit: '',
        reasonForReturn: '',
        otherReasonForReturnNote: '',
    };
    const {classes} = useStyles();
    const {oagCode} = useParams();
    const [name, setName] = useState(initialValues.name);
    const [reasonForVisit, setReasonForVisit] = useState(initialValues.reasonForVisit);
    const [reasonForReturn, setReasonForReturn] = useState(initialValues.reasonForReturn);
    const [otherReasonForReturnNote, setOtherReasonForReturnNote] = useState(initialValues.otherReasonForReturnNote);
    const [isPhoneDialogVisible, setIsPhoneDialogVisible] = useState(false);
    const [checkInButtonDisabled, setCheckInButtonDisabled] = useState(true);
    const [checkInSuccess, setCheckInSuccess] = useState(null);

    useEffect(() => {
        if (name.length === 0) setCheckInButtonDisabled(true)
        else if (reasonForVisit.length === 0) setCheckInButtonDisabled(true)
        else if (reasonForVisit === apiReasonsForVisit.Return && reasonForReturn.length === 0)
            setCheckInButtonDisabled(true)
        else if (reasonForReturn === apiReasonsForReturn.Other && otherReasonForReturnNote.length === 0)
            setCheckInButtonDisabled(true)
        else setCheckInButtonDisabled(false)
    }, [name, reasonForVisit, reasonForReturn, otherReasonForReturnNote]);

    const handleNameInputChange = (event) => {
        setName(event.target.value);
    };

    const handleReasonForVisitInputChange = (event) => {
        setReasonForReturn('');
        setOtherReasonForReturnNote('');
        setReasonForVisit(event.target.value);
    };

    const handleReasonForReturnInputChange = (event) => {
        if (event.target.value !== apiReasonsForReturn.Other) setOtherReasonForReturnNote('');
        setReasonForReturn(event.target.value);
    };

    const handleOtherReasonForReturnNoteInputChange = (event) => {
        setOtherReasonForReturnNote(event.target.value);
    };

    const handleCheckInClick = () => {
        setIsPhoneDialogVisible(true);
    };

    const handleSubmit = (countryCallingCode, phoneNumber) => {
        const baseURL = `${window.location.protocol}//${window.location.hostname}`
        let sanitizedReasonForReturn = reasonForReturn;
        if (sanitizedReasonForReturn.length === 0) sanitizedReasonForReturn = null;

        let sanitizedOtherReasonForReturnNote = otherReasonForReturnNote;
        if (sanitizedOtherReasonForReturnNote.length === 0) sanitizedOtherReasonForReturnNote = null;

        submitCheckIn(baseURL, oagCode ? oagCode : 'oagCode', {
            name,
            reasonForVisit,
            reasonForReturn: sanitizedReasonForReturn,
            otherReasonForReturnNote: sanitizedOtherReasonForReturnNote,
            countryCallingCode,
            phoneNumber
        })
            .then((response) => {
                if (response.ok) {
                    setCheckInSuccess(true);
                } else {
                    setCheckInSuccess(false);
                }
            })
            .catch((e) => {
                setCheckInSuccess(false);
            });
        setIsPhoneDialogVisible(false);
    };

    function renderCheckedInSucceededMessage() {
        return <Grid container xs={12} className={classes.rootGridContainer}>
            <Grid item xs={12}>
                <Typography variant='h4' align='center' sx={{fontWeight: 900}}>
                    You are checked in!
                </Typography>
            </Grid>
            <Grid item xs={12} sx={{textAlign: 'center'}}>
                <HowToReg sx={{fontSize: '10em'}}/>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='subtitle1' align='center' className={classes.subtitle}>
                    Take a seat and the next available representative will see you soon.
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography align='center' className={classes.surveyTitle} data-testid='help-text'>
                    Wait, want to help?
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant='subtitle1' align='center' className={classes.subtitle}
                            data-testid='survey-subtitle'>
                    Please take a quick survey to tell us how we’re doing, how we can improve, or to share tasty
                    recipes.
                </Typography>
            </Grid>
            <Grid item xs={12} align='center'>
                <Link rel="stylesheet" href="https://forms.office.com/Pages/ResponsePage.aspx?id=GauayW03xUmOkwDwO5m167rrcWZNUxZFs3pF_1LeBHdUQlZCS1I3VkswVjg5Wk9IOFVUUzJNVEJERC4u" className={classes.surveyLink}>
                    <Button align='center' variant='contained' color='secondary'
                            data-testid='driverCheckInSurveyButton'
                            className={classes.surveyButton}>
                        CLICK HERE FOR SURVEY
                    </Button>
                </Link>
            </Grid>
        </Grid>;
    }

    function renderCheckInFailedMessage() {
        return <Grid container justifyContent='center'>
            <Grid item xs={12}>
                <Typography align='center' data-testid='driverCheckInFailureText'>
                    We're sorry, we are having trouble getting you checked in.
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography align='center' data-testid='driverCheckInFailureDetails'>
                    Please refresh this page and try again.
                </Typography>
            </Grid>
        </Grid>;
    }

    return (
        <Grid container justifyContent='center'>
            <Grid item lg={6}>
                {checkInSuccess && renderCheckedInSucceededMessage()}
                {checkInSuccess === false && renderCheckInFailedMessage()}
                {checkInSuccess === null && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant='h3'>
                                Hello!
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography data-testid='driverCheckInText'>
                                Please fill out the form below to check-in. Once we know more about you, we will better
                                be able to
                                help!
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        variant='outlined'
                                        name='name'
                                        type='name'
                                        label='Name'
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        fullWidth
                                        required
                                        value={name}
                                        onChange={handleNameInputChange}
                                        data-testid='nameTextInput'
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        variant='outlined'
                                        name='reasonForVisit'
                                        type='reasonForVisit'
                                        label='Reason For Visit'
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        fullWidth
                                        required
                                        value={reasonForVisit}
                                        onChange={handleReasonForVisitInputChange}
                                        data-testid='reasonForVisitDropdown'
                                    >
                                        {reasonForVisitOptions.map(
                                            (reason) => (
                                                <MenuItem key={reason.apiValue.toLowerCase()}
                                                          value={reason.apiValue.toLowerCase()}
                                                          data-testid={`visit-reason-${reason.apiValue.toLowerCase()}`}
                                                >
                                                    {reason.displayValue}
                                                </MenuItem>
                                            ),
                                        )}
                                    </TextField>
                                </Grid>
                                {reasonForVisit === apiReasonsForVisit.Return && (
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            variant='outlined'
                                            name='reasonForReturn'
                                            type='reasonForReturn'
                                            label='Reason For Return'
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            fullWidth
                                            required
                                            value={reasonForReturn}
                                            onChange={handleReasonForReturnInputChange}
                                            data-testid='reasonForReturnDropdown'
                                        >
                                            {reasonForReturnOptions.map(
                                                (reason) => (
                                                    <MenuItem key={reason.apiValue.toLowerCase()}
                                                              value={reason.apiValue.toLowerCase()}>
                                                        {reason.displayValue}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </TextField>
                                    </Grid>
                                )}
                                {reasonForReturn === apiReasonsForReturn.Other && (
                                    <Grid item xs={12}>
                                        <TextField
                                            variant='outlined'
                                            name='otherReasonForReturnNote'
                                            type='otherReasonForReturnNote'
                                            label='Other Reason'
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            inputProps={{maxLength: 280}}
                                            fullWidth
                                            required
                                            value={otherReasonForReturnNote}
                                            onChange={handleOtherReasonForReturnNoteInputChange}
                                            data-testid='otherReasonForReturnNoteTextInput'
                                        />
                                    </Grid>
                                )}
                                <Grid item>
                                    <Button
                                        onClick={handleCheckInClick}
                                        size='medium'
                                        variant='contained'
                                        color='secondary'
                                        id='checkIn'
                                        disabled={checkInButtonDisabled}
                                        data-testid='driverCheckInButton'
                                    >
                                        Check-in
                                    </Button>
                                </Grid>
                            </Grid>
                            <PhoneNumberSubmissionDialog isOpen={isPhoneDialogVisible} onClose={handleSubmit}/>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Grid>
    )
}
